import { useEffect, useMemo, useState } from "react";
import {
  collection,
  getDocs,
} from "firebase/firestore";
import { useNavigate } from "react-router-dom";
import { db } from "../firebase";

function AdminDashboard() {
  const navigate = useNavigate();

  const [employees, setEmployees] = useState([]);
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadDashboardData = async () => {
      try {
        setLoading(true);

        const [
          employeeSnapshot,
          leadSnapshot,
        ] = await Promise.all([
          getDocs(collection(db, "employees")),
          getDocs(collection(db, "leads")),
        ]);

        setEmployees(
          employeeSnapshot.docs.map((item) => ({
            id: item.id,
            ...item.data(),
          }))
        );

        setLeads(
          leadSnapshot.docs.map((item) => ({
            id: item.id,
            ...item.data(),
          }))
        );
      } catch (error) {
        console.error(
          "Admin dashboard load error:",
          error
        );
      } finally {
        setLoading(false);
      }
    };

    loadDashboardData();
  }, []);

  const convertToDate = (value) => {
    if (!value) {
      return null;
    }

    try {
      if (typeof value.toDate === "function") {
        return value.toDate();
      }

      const date = new Date(value);

      if (Number.isNaN(date.getTime())) {
        return null;
      }

      return date;
    } catch (error) {
      return null;
    }
  };

  const getLocalDateKey = (value) => {
    const date = convertToDate(value);

    if (!date) {
      return "";
    }

    const year = date.getFullYear();

    const month = String(
      date.getMonth() + 1
    ).padStart(2, "0");

    const day = String(
      date.getDate()
    ).padStart(2, "0");

    return `${year}-${month}-${day}`;
  };

  const todayKey = getLocalDateKey(
    new Date()
  );

  const isWorkingToday = (employee) => {
    if (employee.workStatus !== "Working") {
      return false;
    }

    return (
      getLocalDateKey(
        employee.workStartedAt
      ) === todayKey
    );
  };

  const isOnBreakToday = (employee) => {
    if (employee.workStatus !== "On Break") {
      return false;
    }

    return (
      getLocalDateKey(
        employee.breakStartedAt
      ) === todayKey
    );
  };

  const getDisplayStatus = (employee) => {
    if (isOnBreakToday(employee)) {
      return "On Break";
    }

    if (isWorkingToday(employee)) {
      return "Working";
    }

    return "Work Ended";
  };

  const callDoneToday = (lead) => {
    const history = Array.isArray(
      lead.remarksHistory
    )
      ? lead.remarksHistory
      : [];

    return history.some(
      (historyItem) =>
        getLocalDateKey(
          historyItem.createdAt
        ) === todayKey
    );
  };

  const stats = useMemo(() => {
    const activeEmployees =
      employees.filter(
        (employee) =>
          employee.status !== "Inactive"
      );

    return {
      totalEmployees:
        activeEmployees.length,

      currentlyWorking:
        activeEmployees.filter(
          isWorkingToday
        ).length,

      onBreak:
        activeEmployees.filter(
          isOnBreakToday
        ).length,

      totalLeads:
        leads.length,

      callsDoneToday:
        leads.filter(
          callDoneToday
        ).length,

      pendingLeads:
        leads.filter(
          (lead) =>
            lead.status !== "Converted" &&
            lead.status !== "Prospect" &&
            lead.status !== "Not Interested"
        ).length,

      pendingCallbacks:
        leads.filter(
          (lead) =>
            lead.callbackStatus ===
            "Pending"
        ).length,

      convertedLeads:
        leads.filter(
          (lead) =>
            lead.status === "Converted"
        ).length,
    };
  }, [employees, leads, todayKey]);

  const dashboardCards = [
    {
      title: "Total Employees",
      value: stats.totalEmployees,
      subtitle: `Active accounts: ${stats.totalEmployees}`,
      type: "employees",
    },
    {
      title: "Currently Working",
      value: stats.currentlyWorking,
      subtitle: "Employees working today",
      type: "working",
    },
    {
      title: "On Break",
      value: stats.onBreak,
      subtitle: "Employees on tagged break",
      type: "break",
    },
    {
      title: "Total Leads",
      value: stats.totalLeads,
      subtitle: "All customer records",
      type: "leads",
    },
    {
      title: "Calls Done Today",
      value: stats.callsDoneToday,
      subtitle: "Leads updated today",
      type: "calls",
    },
    {
      title: "Pending Leads",
      value: stats.pendingLeads,
      subtitle: "Customers still needing action",
      type: "pending-leads",
    },
    {
      title: "Pending Callbacks",
      value: stats.pendingCallbacks,
      subtitle: "Callbacks not completed",
      type: "callbacks",
    },
    {
      title: "Converted Leads",
      value: stats.convertedLeads,
      subtitle: "Successful conversions",
      type: "converted",
    },
  ];

  const employeeRows = useMemo(() => {
    return employees
      .filter(
        (employee) =>
          employee.status !== "Inactive"
      )
      .map((employee) => {
        const employeeLeads =
          leads.filter(
            (lead) =>
              lead.assignedEmployeeId ===
              employee.id
          );

        return {
          ...employee,

          displayStatus:
            getDisplayStatus(employee),

          assigned:
            employeeLeads.length,

          callsDoneToday:
            employeeLeads.filter(
              callDoneToday
            ).length,

          pending:
            employeeLeads.filter(
              (lead) =>
                lead.status !== "Converted" &&
                lead.status !== "Prospect" &&
                lead.status !== "Not Interested"
            ).length,

          callbacks:
            employeeLeads.filter(
              (lead) =>
                lead.callbackStatus ===
                "Pending"
            ).length,

          connected:
            employeeLeads.filter(
              (lead) =>
                lead.callConnectionType ===
                "Connected"
            ).length,

          converted:
            employeeLeads.filter(
              (lead) =>
                lead.status === "Converted"
            ).length,
        };
      });
  }, [
    employees,
    leads,
    todayKey,
  ]);

  const formatActivity = (value) => {
    const date = convertToDate(value);

    if (!date) {
      return "-";
    }

    return date.toLocaleString(
      "en-IN",
      {
        day: "2-digit",
        month: "short",
        hour: "2-digit",
        minute: "2-digit",
      }
    );
  };

  const quickCardStyle = {
    background: "#ffffff",
    border: "1px solid #d9eaff",
    borderRadius: "16px",
    padding: "20px",
    minHeight: "145px",
    cursor: "pointer",
    textAlign: "left",
    boxShadow: "0 2px 8px rgba(0,0,0,0.03)",
  };

  const quickNumberStyle = {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    minWidth: "42px",
    height: "42px",
    padding: "0 10px",
    borderRadius: "12px",
    background: "#e0f2fe",
    color: "#0284c7",
    fontWeight: "800",
    marginBottom: "14px",
  };

  return (
    <div className="admin-dashboard-page">
      <header className="dashboard-header">
        <div>
          <h1>
            Trading Wealth CRM
          </h1>

          <p>
            Admin Dashboard
          </p>
        </div>

        <button
          type="button"
          className="logout-button"
          onClick={() => {
            localStorage.removeItem(
              "crmAdmin"
            );

            navigate("/");
          }}
        >
          Logout
        </button>
      </header>

      <main className="dashboard-main">
        <section className="dashboard-welcome">
          <p className="dashboard-label">
            ADMIN CONTROL PANEL
          </p>

          <h2>
            Welcome back, Admin
          </h2>

          <p>
            Monitor employee attendance,
            assigned leads, calls,
            callbacks and conversions.
          </p>
        </section>

        <section className="dashboard-stats">
          {dashboardCards.map((card) => (
            <div
              key={card.type}
              className="dashboard-stat-card"
              style={{
                cursor: "pointer",
              }}
              onClick={() =>
                navigate(
                  `/admin-reports?type=${card.type}`
                )
              }
            >
              <p>
                {card.title}
              </p>

              <h3>
                {loading
                  ? "..."
                  : card.value}
              </h3>

              <span>
                {card.subtitle}
              </span>
            </div>
          ))}
        </section>

        <section className="dashboard-section">
          <div className="section-heading">
            <div>
              <p className="dashboard-label">
                QUICK ACTIONS
              </p>

              <h2>
                Manage CRM
              </h2>
            </div>
          </div>

          <div
            style={{
              display: "grid",
              gridTemplateColumns:
                "repeat(auto-fit, minmax(230px, 280px))",
              gap: "16px",
              marginTop: "15px",
            }}
          >
            <div
              style={
                quickCardStyle
              }
              onClick={() =>
                navigate(
                  "/manage-employees"
                )
              }
            >
              <span
                style={
                  quickNumberStyle
                }
              >
                01
              </span>

              <h3
                style={{
                  margin:
                    "0 0 8px",
                }}
              >
                Manage Employees
              </h3>

              <p
                style={{
                  margin: 0,
                  color:
                    "#64748b",
                  lineHeight:
                    "1.5",
                }}
              >
                Add, edit, activate or
                deactivate employees.
              </p>
            </div>

            <div
              style={
                quickCardStyle
              }
              onClick={() =>
                navigate(
                  "/manage-leads"
                )
              }
            >
              <span
                style={
                  quickNumberStyle
                }
              >
                02
              </span>

              <h3
                style={{
                  margin:
                    "0 0 8px",
                }}
              >
                Manage Leads
              </h3>

              <p
                style={{
                  margin: 0,
                  color:
                    "#64748b",
                  lineHeight:
                    "1.5",
                }}
              >
                Add, assign, edit and
                churn customer leads.
              </p>
            </div>
          </div>
        </section>

        <section className="dashboard-section">
          <div className="section-heading">
            <div>
              <p className="dashboard-label">
                LIVE EMPLOYEE MONITORING
              </p>

              <h2>
                Daily Performance
              </h2>
            </div>
          </div>

          <div className="employee-table-wrapper">
            <table className="employee-table">
              <thead>
                <tr>
                  <th>Employee</th>
                  <th>Work Status</th>
                  <th>Assigned</th>
                  <th>Calls Done Today</th>
                  <th>Pending</th>
                  <th>Callbacks</th>
                  <th>Connected</th>
                  <th>Converted</th>
                  <th>Last Activity</th>
                </tr>
              </thead>

              <tbody>
                {employeeRows.length === 0 ? (
                  <tr>
                    <td colSpan="9">
                      No employees found.
                    </td>
                  </tr>
                ) : (
                  employeeRows.map(
                    (employee) => (
                      <tr
                        key={
                          employee.id
                        }
                      >
                        <td>
                          <strong>
                            {
                              employee.name
                            }
                          </strong>

                          <small>
                            {
                              employee.email
                            }
                          </small>
                        </td>

                        <td>
                          <span
                            className={
                              employee.displayStatus ===
                              "Working"
                                ? "status-badge active"
                                : employee.displayStatus ===
                                    "On Break"
                                  ? "status-badge break"
                                  : "status-badge inactive"
                            }
                          >
                            {
                              employee.displayStatus
                            }
                          </span>
                        </td>

                        <td>
                          {
                            employee.assigned
                          }
                        </td>

                        <td>
                          {
                            employee.callsDoneToday
                          }
                        </td>

                        <td>
                          {
                            employee.pending
                          }
                        </td>

                        <td>
                          {
                            employee.callbacks
                          }
                        </td>

                        <td>
                          {
                            employee.connected
                          }
                        </td>

                        <td>
                          {
                            employee.converted
                          }
                        </td>

                        <td>
                          {formatActivity(
                            employee.lastActivityAt
                          )}
                        </td>
                      </tr>
                    )
                  )
                )}
              </tbody>
            </table>
          </div>
        </section>
      </main>
    </div>
  );
}

export default AdminDashboard;